import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { isUploadFilePreviewable, encodeFilePreviewUrl } from 'src/app/shared/helpers/upload-file-preview.util';
import { FloatingUploadPreviewService } from 'src/app/shared/service/floating-upload-preview/floating-upload-preview.service';
import {
  formatUploadedFileSize,
  isPreviewableUploadedFileUrl,
} from '../cargo-heating-onboarding-view.util';
import { LIQUID_CARGO_EMPTY_MESSAGE } from '../cargo-heating-liquid-cargo.util';
import {
  OnboardingDetailRow,
  OnboardingDetailSection,
  OnboardingDetailTable,
  OnboardingSectionSavePayload,
  OnboardingUploadedFile,
  isEmptyOnboardingDisplayValue,
} from '../onboarding-vessel-mapper.util';

@Component({
  selector: 'app-onboarding-editable-section',
  templateUrl: './onboarding-editable-section.component.html',
  styleUrls: ['./onboarding-editable-section.component.scss'],
})
export class OnboardingEditableSectionComponent implements OnChanges {
  @Input() section!: OnboardingDetailSection;
  @Input() isDarkMode = false;
  @Input() saving = false;
  @Output() save = new EventEmitter<OnboardingSectionSavePayload>();

  isEditing = false;
  draftRows: OnboardingDetailRow[] = [];
  draftTable: OnboardingDetailTable | null = null;
  private wasSaving = false;

  constructor(
    private floatingPreview: FloatingUploadPreviewService,
    private toastr: ToastrService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['section'] && !changes['section'].firstChange) {
      this.cancelEdit();
    }
    if (changes['saving'] && this.wasSaving && !this.saving) {
      this.isEditing = false;
      this.draftRows = [];
      this.draftTable = null;
      this.wasSaving = false;
    }
    if (this.saving) {
      this.wasSaving = true;
    }
  }

  get canEdit(): boolean {
    return this.section.editable !== false && !this.section.readOnly;
  }

  get hasKvRows(): boolean {
    const rows = this.isEditing ? this.draftRows : this.section.rows;
    return !!rows?.length;
  }

  get activeTable(): OnboardingDetailTable | null {
    return this.isEditing ? this.draftTable : this.section.table ?? null;
  }

  get hasTableRows(): boolean {
    return !!this.activeTable?.rows?.length;
  }

  get uploadedFiles(): OnboardingUploadedFile[] {
    return this.section.files ?? [];
  }

  get emptyStateMessage(): string {
    if (this.section.emptyMessage) {
      return this.section.emptyMessage;
    }
    if (this.section.id === 'liquid-cargo-table') {
      return LIQUID_CARGO_EMPTY_MESSAGE;
    }
    if (this.section.id === 'tank-heating-grid') {
      return 'No tank / heating grid data available.';
    }
    return 'No data available for this section.';
  }

  startEdit(): void {
    if (!this.canEdit) {
      return;
    }
    this.draftRows = (this.section.rows ?? []).map((row) => ({
      ...row,
      value: row.type === 'number' && isEmptyOnboardingDisplayValue(row.value) ? '' : row.value,
    }));
    this.draftTable = this.section.table
      ? {
          ...this.section.table,
          rows: this.section.table.rows.map((row) => [...row]),
        }
      : null;
    this.isEditing = true;
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.draftRows = [];
    this.draftTable = null;
  }

  saveSection(): void {
    if (!this.isEditing || this.saving) {
      return;
    }
    this.save.emit({
      sectionId: this.section.id,
      rows: this.draftRows,
      table: this.draftTable,
    });
  }

  trackRow(_index: number, row: OnboardingDetailRow): string {
    return row.label;
  }

  trackFile(_index: number, file: OnboardingUploadedFile): string {
    return `${file.field}-${file.fileName}-${file.s3Url}`;
  }

  formatFileSize(bytes?: number): string {
    return formatUploadedFileSize(bytes);
  }

  fileTypeLabel(file: OnboardingUploadedFile): string {
    const mime = String(file.mimeType ?? '').trim();
    if (mime) {
      const subtype = mime.split('/').pop();
      return subtype ? subtype.toUpperCase() + ' Document' : mime;
    }
    const ext = file.fileName.split('.').pop();
    return ext ? `${ext.toUpperCase()} Document` : 'Document';
  }

  fileIcon(file: OnboardingUploadedFile): string {
    const mime = String(file.mimeType ?? '').toLowerCase();
    const name = file.fileName.toLowerCase();
    if (mime.includes('pdf') || name.endsWith('.pdf')) {
      return 'picture_as_pdf';
    }
    if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(name)) {
      return 'image';
    }
    return 'description';
  }

  canPreviewFile(file: OnboardingUploadedFile): boolean {
    return (
      isPreviewableUploadedFileUrl(file.s3Url) &&
      isUploadFilePreviewable(file.fileName)
    );
  }

  canDownloadFile(file: OnboardingUploadedFile): boolean {
    return isPreviewableUploadedFileUrl(file.s3Url);
  }

  previewFile(file: OnboardingUploadedFile, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canPreviewFile(file)) {
      this.toastr.info('Preview URL not available for this file.', 'Preview');
      return;
    }
    this.floatingPreview.open({
      fileName: file.fileName,
      previewUrl: file.s3Url,
    });
  }

  downloadFile(file: OnboardingUploadedFile, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canDownloadFile(file)) {
      this.toastr.info('Download URL not available for this file.', 'Download');
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = encodeFilePreviewUrl(file.s3Url);
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.download = file.fileName || 'download';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
}
